import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../utils/supabase'
import { 
  Search, Activity, Smartphone, MapPin, Users,
  RefreshCw, Filter, Download, TrendingUp,
  Signal, SignalHigh, SignalLow, SignalZero,
  ExternalLink, LayoutDashboard, Truck, Mail, Calendar, Sun, Monitor
} from 'lucide-react'
import Icon3D from '../components/Icon3D'

export default function Dashboard() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lastSync, setLastSync] = useState(null)

  const fetchStats = useCallback(async () => {
    try {
      const data = await api.get('/api/stats/dashboard')
      if (data.success) {
        const raw = data.data
        setStats({
          timestamp: raw.last_sync,
          trackers: {
            total:        raw.total_trackers    || 0,
            with_line:    raw.trackers_with_line || 0,
            without_line: (raw.total_trackers || 0) - (raw.trackers_with_line || 0),
            by_status: {
              online:  raw.online  || 0,
              idle:    raw.idle    || 0,
              offline: raw.offline || 0
            }
          },
          lines: {
            toka:   raw.total_toka   || 0,
            oui:    raw.total_oui    || 0,
            telcel: raw.total_telcel || 0,
            allce:  raw.total_allce  || 0
          },
          recent_trackers: raw.recent_trackers || [],
          recent_syncs:    raw.recent_syncs    || []
        })
      }
    } catch (err) {
      console.error('Error fetching stats:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleSync = async () => {
    try {
      setLoading(true)
      const res = await api.post('/api/sync/manual', {})
      if (res.success) {
        setLastSync(new Date())
        await fetchStats()
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
    const interval = setInterval(fetchStats, 30000) // Refrescar cada 30s
    return () => clearInterval(interval)
  }, [fetchStats])

  const StatCard = ({ icon: Icon, label, value, sublabel, color = 'amber' }) => {
    return (
      <div className="card card-hover p-5 overflow-hidden">
        <div className="flex items-start justify-between">
          <Icon3D icon={Icon} color={color} size="lg" />
          {loading && <RefreshCw className="w-5 h-5 text-gray-500 animate-spin" />}
        </div>
        <div className="mt-4">
          <p className="text-3xl font-extrabold text-white tracking-tight">{value.toLocaleString()}</p>
          <p className="text-sm text-gray-400 mt-1">{label}</p>
          {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
        </div>
      </div>
    )
  }

  const trackerStats = stats?.trackers || {}
  const lineStats = stats?.lines || {}

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 text-sm">
            Última actualización: {stats?.timestamp ? new Date(stats.timestamp).toLocaleString() : 'N/A'}
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={loading}
          className="btn btn-primary"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Sincronizar CSV
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          icon={Activity} 
          label="Total Trackers" 
          value={trackerStats.total || 0}
          sublabel={`${trackerStats.with_line || 0} con línea | ${trackerStats.without_line || 0} sin línea`}
        />
        <StatCard 
          icon={MapPin} 
          label="Online" 
          value={trackerStats.by_status?.online || 0}
          color="emerald"
          sublabel={`${trackerStats.by_status?.idle || 0} idle | ${trackerStats.by_status?.offline || 0} offline`}
        />
        <StatCard 
          icon={Smartphone} 
          label="Líneas TOKA" 
          value={lineStats.toka || 0}
          color="red"
          sublabel="SIM Roja"
        />
        <StatCard 
          icon={Smartphone} 
          label="Líneas OUI" 
          value={lineStats.oui || 0}
          color="yellow"
          sublabel="SIM Amarilla"
        />
      </div>

      {/* Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trackers by Status */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Icon3D icon={Activity} color="blue" size="sm" />
            Estado de Conexión
          </h3>
          <div className="space-y-3">
            {trackerStats.by_status && Object.entries(trackerStats.by_status).map(([status, count]) => {
              const total = trackerStats.total || 1
              const percent = Math.round((count / total) * 100)
              const colors = {
                online: 'bg-emerald-500',
                idle: 'bg-yellow-500',
                offline: 'bg-red-500'
              }
              return (
                <div key={status} className="flex items-center gap-3">
                  <span className="w-20 text-sm text-gray-400 capitalize">{status}</span>
                  <div className="flex-1 bg-white/10 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${colors[status] || 'bg-gray-500'}`} 
                      style={{width: `${percent}%`}}
                    />
                  </div>
                  <span className="w-12 text-right text-sm text-white font-medium">{count}</span>
                  <span className="w-12 text-right text-xs text-gray-500">{percent}%</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Lines Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Icon3D icon={Smartphone} color="violet" size="sm" />
            Distribución por Tipo de Línea
          </h3>
          <div className="space-y-3">
            {[
              { label: 'TOKA SIM', count: lineStats.toka || 0, color: 'bg-red-500' },
              { label: 'OUI', count: lineStats.oui || 0, color: 'bg-yellow-500' },
              { label: 'Telcel B2B', count: lineStats.telcel || 0, color: 'bg-blue-500' },
              { label: 'ALLCE', count: lineStats.allce || 0, color: 'bg-purple-500' },
              { label: 'Sin Línea', count: trackerStats.without_line || 0, color: 'bg-gray-500' }
            ].map((item) => {
              const total = (trackerStats.total || 1)
              const percent = Math.round((item.count / total) * 100)
              return (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="w-24 text-sm text-gray-400">{item.label}</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${item.color}`} 
                      style={{width: `${percent}%`}}
                    />
                  </div>
                  <span className="w-12 text-right text-sm text-white font-medium">{item.count}</span>
                  <span className="w-12 text-right text-xs text-gray-500">{percent}%</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Apps VPS - Enlaces a otras aplicaciones */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-5">
          <Icon3D icon={LayoutDashboard} color="amber" size="md" />
          <h3 className="text-xl font-bold text-white">Apps Yango GPS</h3>
          <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded-full ml-auto">
            VPS Server
          </span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            { 
              icon: LayoutDashboard, 
              label: 'Dashboard Principal', 
              url: 'https://app.yangogps.com',
              desc: 'Lomdu & Yango GPS',
              color: 'blue',
              gradient: 'from-blue-500/20 to-cyan-500/20',
              border: 'border-blue-500/30'
            },
            { 
              icon: Truck, 
              label: 'Guadalajara', 
              url: 'https://guadalajara.yangogps.com',
              desc: 'Pegaso Packing',
              color: 'amber',
              gradient: 'from-amber-500/20 to-orange-500/20',
              border: 'border-amber-500/30'
            },
            { 
              icon: Smartphone, 
              label: 'Líneas', 
              url: 'https://lineas.yangogps.com',
              desc: 'Trackers & Líneas',
              color: 'green',
              gradient: 'from-green-500/20 to-emerald-500/20',
              border: 'border-green-500/30',
              active: true
            },
            { 
              icon: Mail, 
              label: 'Webmail', 
              url: 'https://webmail.yangogps.com',
              desc: 'Correo Corporativo',
              color: 'purple',
              gradient: 'from-purple-500/20 to-violet-500/20',
              border: 'border-purple-500/30'
            },
            { 
              icon: Calendar, 
              label: 'Calendario', 
              url: 'https://calendar.yangogps.com',
              desc: 'Agenda Compartida',
              color: 'pink',
              gradient: 'from-pink-500/20 to-rose-500/20',
              border: 'border-pink-500/30'
            },
            { 
              icon: Sun, 
              label: 'Solar', 
              url: 'https://solar.yangogps.com',
              desc: 'Monitoreo Solar',
              color: 'yellow',
              gradient: 'from-yellow-500/20 to-amber-500/20',
              border: 'border-yellow-500/30'
            },
            { 
              icon: Monitor, 
              label: 'Paneles', 
              url: 'https://paneles.yangogps.com',
              desc: 'Panel de Control',
              color: 'cyan',
              gradient: 'from-cyan-500/20 to-teal-500/20',
              border: 'border-cyan-500/30'
            },
            { 
              icon: Activity, 
              label: 'API Server', 
              url: 'https://api.yangogps.com',
              desc: 'API Endpoints',
              color: 'indigo',
              gradient: 'from-indigo-500/20 to-blue-500/20',
              border: 'border-indigo-500/30'
            }
          ].map((app) => (
            <a
              key={app.label}
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`group relative flex flex-col gap-3 p-5 rounded-xl 
                         bg-gradient-to-br ${app.gradient}
                         ${app.border} border-2
                         hover:scale-[1.02] hover:shadow-xl
                         transition-all duration-300
                         ${app.active ? 'ring-2 ring-white/20' : ''}`}
            >
              <div className="flex items-start justify-between">
                <Icon3D icon={app.icon} color={app.color} size="lg" />
                <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
              </div>
              
              <div className="mt-auto">
                <h4 className="font-bold text-white transition-colors">
                  {app.label}
                </h4>
                <p className="text-xs text-gray-400 group-hover:text-gray-300 mt-1">
                  {app.desc}
                </p>
              </div>
              
              {app.active && (
                <div className="absolute top-3 right-3 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              )}
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}
